import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Header from '../components/Header';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { itineraryApi } from '../services/api';
import { toast } from 'sonner';
import { downloadBudgetPdf } from '../utils/downloadBudgetPdf';
import {
  Wallet, MapPin, Calendar, ChevronRight, Loader2, Trash2, Share2,
  ArrowLeft, DollarSign, PieChart, X, Copy, MessageCircle,
  Twitter, Facebook, Link2, Mail, FileText, Download, Edit3,
  UtensilsCrossed, Car, Hotel as HotelIcon, Ticket, ShoppingBag, MoreHorizontal, Plane,
} from 'lucide-react';

const CATEGORY_META = {
  food: { icon: UtensilsCrossed, color: 'bg-orange-500', label: 'Food' },
  flights: { icon: Plane, color: 'bg-indigo-500', label: 'Flights' },
  transport: { icon: Car, color: 'bg-blue-500', label: 'Transport' },
  accommodation: { icon: HotelIcon, color: 'bg-purple-500', label: 'Stay' },
  activities: { icon: Ticket, color: 'bg-teal-500', label: 'Activities' },
  shopping: { icon: ShoppingBag, color: 'bg-pink-500', label: 'Shopping' },
  other: { icon: MoreHorizontal, color: 'bg-gray-500', label: 'Other' },
};

const fadeUp = { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.45 } } };
const stagger = { show: { transition: { staggerChildren: 0.08 } } };

const BudgetCard = ({ budget, onRefresh }) => {
  const navigate = useNavigate();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);

  const handleEdit = (e) => {
    e.stopPropagation();
    navigate(`/itinerary/${budget.itinerary_id}/budget`);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await itineraryApi.deleteBudgetExpenses(budget.itinerary_id);
      toast.success('Budget expenses cleared');
      onRefresh();
    } catch { toast.error('Failed to delete budget'); }
    finally { setDeleting(false); setDeleteOpen(false); }
  };

  const handleDownloadPdf = async (e) => {
    if (e) e.stopPropagation();
    try {
      const [expRes, itinRes, sumRes] = await Promise.all([
        itineraryApi.listExpenses(budget.itinerary_id),
        itineraryApi.get(budget.itinerary_id),
        itineraryApi.getBudgetSummary(budget.itinerary_id),
      ]);
      downloadBudgetPdf(budget.itinerary_id, itinRes.data, expRes.data, sumRes.data);
      toast.success('Generating PDF...');
    } catch { toast.error('Failed to download PDF'); }
  };

  const shareUrl = `${window.location.origin}/itinerary/${budget.itinerary_id}/budget`;
  const shareText = `Check out my travel budget for ${budget.destination_city}! Tracked with Triple A Travel Solutions.`;
  const shareWhatsApp = () => window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(shareText + '\n' + shareUrl)}`, '_blank');
  const shareTwitter = () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, '_blank');
  const shareFacebook = () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`, '_blank');
  const shareEmail = () => {
    const subject = encodeURIComponent(`My Travel Budget - ${budget.destination_city}`);
    const body = encodeURIComponent(`${shareText}\n\nView my budget: ${shareUrl}`);
    window.open(`mailto:?subject=${subject}&body=${body}`, '_self');
  };
  const copyShareLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success('Link copied!'); }
    catch { toast.error('Failed to copy'); }
  };

  // Top 3 categories for mini bar
  const topCategories = Object.entries(budget.by_category || {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3);

  return (
    <motion.div variants={fadeUp}>
      <Card
        className="bg-white/95 backdrop-blur-xl border border-white/30 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer group overflow-hidden"
        onClick={() => navigate(`/itinerary/${budget.itinerary_id}/budget`)}
        data-testid={`budget-card-${budget.itinerary_id}`}
      >
        {/* Gradient header */}
        <div className="h-28 bg-gradient-to-br from-[#0B3C5D] via-[#1a6b5a] to-[#0d4f4f] relative overflow-hidden p-5 flex flex-col justify-between">
          <div className="absolute top-4 right-4 w-20 h-20 bg-white/5 rounded-full blur-xl" />
          <div>
            <h3 className="font-serif text-lg text-white font-bold truncate">{budget.destination_city}</h3>
            {budget.destination_country && (
              <p className="text-white/50 text-xs flex items-center gap-1 mt-0.5">
                <MapPin className="w-3 h-3" /> {budget.destination_country}
              </p>
            )}
          </div>
          <div className="flex items-end justify-between">
            <span className="text-2xl font-bold text-white font-mono">
              ${budget.total_spent.toFixed(2)}
            </span>
            <span className="text-xs text-white/50 bg-white/10 px-2 py-0.5 rounded-full">
              {budget.expense_count} expense{budget.expense_count !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        <CardContent className="p-4">
          {/* Category mini bars */}
          {topCategories.length > 0 ? (
            <div className="space-y-1.5 mb-3">
              {topCategories.map(([cat, amount]) => {
                const meta = CATEGORY_META[cat] || CATEGORY_META.other;
                const Icon = meta.icon;
                const pct = budget.total_spent > 0 ? (amount / budget.total_spent) * 100 : 0;
                return (
                  <div key={cat} className="flex items-center gap-2">
                    <div className={`w-5 h-5 ${meta.color} rounded flex items-center justify-center text-white flex-shrink-0`}>
                      <Icon className="w-3 h-3" />
                    </div>
                    <div className="flex-1 bg-gray-100 rounded-full h-1.5">
                      <div className={`${meta.color} h-1.5 rounded-full`} style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-[10px] text-charcoal/50 w-10 text-right">${amount.toFixed(0)}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="py-3 text-center">
              <p className="text-xs text-charcoal/40">No expenses tracked yet</p>
            </div>
          )}

          {/* Date + Actions */}
          <div className="flex items-center justify-between pt-2 border-t border-gray-100">
            <div className="flex items-center gap-2 text-xs text-charcoal/50">
              {budget.start_date && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> {budget.start_date}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={handleEdit}
                className="w-7 h-7 rounded-full bg-ocean/10 hover:bg-ocean/20 text-ocean flex items-center justify-center transition-colors"
                data-testid={`edit-budget-${budget.itinerary_id}`}>
                <Edit3 className="w-3.5 h-3.5" />
              </button>
              <button onClick={(e) => { e.stopPropagation(); setShowShareDialog(true); }}
                className="w-7 h-7 rounded-full bg-ocean/10 hover:bg-ocean/20 text-ocean flex items-center justify-center transition-colors"
                data-testid={`share-budget-${budget.itinerary_id}`}>
                <Share2 className="w-3.5 h-3.5" />
              </button>
              <button onClick={(e) => { e.stopPropagation(); setDeleteOpen(true); }}
                className="w-7 h-7 rounded-full bg-red-50 hover:bg-red-100 text-red-400 hover:text-red-600 flex items-center justify-center transition-colors"
                data-testid={`delete-budget-${budget.itinerary_id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
              <ChevronRight className="w-4 h-4 text-ocean/30 group-hover:text-ocean group-hover:translate-x-0.5 transition-all" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Delete Dialog */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent onClick={e => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear Budget Expenses?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete all {budget.expense_count} expense(s) for "{budget.destination_city}". The trip itself is not affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting}
              className="bg-red-500 hover:bg-red-600 text-white">
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Share Dialog */}
      <AnimatePresence>
        {showShareDialog && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={() => setShowShareDialog(false)}
          >
            <motion.div
              initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
              className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl"
              onClick={e => e.stopPropagation()}
              data-testid={`share-dialog-budget-${budget.itinerary_id}`}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-ocean font-semibold text-lg flex items-center gap-2">
                  <Share2 className="w-5 h-5 text-gold" /> Share Budget
                </h3>
                <button onClick={() => setShowShareDialog(false)} className="text-charcoal/40 hover:text-charcoal">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-charcoal/60 text-sm mb-2">Share your budget tracker for <strong>{budget.destination_city}</strong>:</p>

              {/* PDF download */}
              <div className="bg-ocean/5 border border-ocean/15 rounded-xl p-4 mb-4 flex items-center gap-3" data-testid={`budget-pdf-display-${budget.itinerary_id}`}>
                <div className="w-10 h-10 bg-ocean/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-ocean" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-ocean truncate">{budget.destination_city} - Budget Report.pdf</p>
                  <p className="text-xs text-charcoal/50">PDF Budget Report</p>
                </div>
                <Button onClick={handleDownloadPdf} size="sm" className="bg-gold hover:bg-gold/90 text-ocean rounded-full text-xs px-4 h-8 font-semibold shadow" data-testid={`budget-download-pdf-${budget.itinerary_id}`}>
                  <Download className="w-3.5 h-3.5 mr-1" /> Download
                </Button>
              </div>

              <p className="text-charcoal/60 text-sm mb-2">Share link:</p>
              <div className="flex gap-2 mb-4">
                <input type="text" readOnly value={shareUrl}
                  className="flex-1 bg-gray-100 rounded-lg px-3 py-2 text-sm text-charcoal border border-gray-200"
                  data-testid="budget-share-url-input" />
                <Button onClick={copyShareLink} className="bg-ocean hover:bg-ocean-light text-white rounded-lg" data-testid="budget-copy-link-btn">
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button onClick={shareWhatsApp} variant="outline" className="rounded-xl h-11 border-green-300 text-green-700 hover:bg-green-50" data-testid="budget-whatsapp-btn">
                  <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
                </Button>
                <Button onClick={shareTwitter} variant="outline" className="rounded-xl h-11 border-sky-300 text-sky-700 hover:bg-sky-50" data-testid="budget-twitter-btn">
                  <Twitter className="w-4 h-4 mr-2" /> Twitter / X
                </Button>
                <Button onClick={shareFacebook} variant="outline" className="rounded-xl h-11 border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="budget-facebook-btn">
                  <Facebook className="w-4 h-4 mr-2" /> Facebook
                </Button>
                <Button onClick={copyShareLink} variant="outline" className="rounded-xl h-11 border-ocean/20 text-ocean hover:bg-ocean/5" data-testid="budget-copylink-btn">
                  <Link2 className="w-4 h-4 mr-2" /> Copy Link
                </Button>
                <Button onClick={shareEmail} variant="outline" className="rounded-xl h-11 border-amber-300 text-amber-700 hover:bg-amber-50 col-span-2" data-testid="budget-email-btn">
                  <Mail className="w-4 h-4 mr-2" /> Email to Friends
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default function MyBudgetsPage() {
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchBudgets = useCallback(async () => {
    try {
      const res = await itineraryApi.listMyBudgets();
      if (res.data.success) setBudgets(res.data.budgets);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchBudgets(); }, [fetchBudgets]);

  // Only show trips that have expenses OR all completed trips as trackable
  const withExpenses = budgets.filter(b => b.expense_count > 0);
  const withoutExpenses = budgets.filter(b => b.expense_count === 0);

  return (
    <div className="min-h-screen relative">
      <div className="fixed inset-0 -z-10 bg-gradient-to-br from-[#0B3C5D] via-[#1a6b5a] to-[#0d4f4f]" />
      <Header />

      <main className="pt-24 pb-20 px-4 sm:px-6 md:px-12 lg:px-24">
        <div className="max-w-5xl mx-auto">
          <div className="mb-6">
            <button onClick={() => navigate('/dashboard')} className="text-white/70 hover:text-white text-sm flex items-center gap-1 transition-colors" data-testid="back-to-dashboard-budgets">
              <ArrowLeft className="w-4 h-4" /> Back to Dashboard
            </button>
          </div>

          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
            <h1 className="font-serif text-3xl sm:text-4xl text-white font-bold flex items-center gap-3" data-testid="budgets-page-title">
              <Wallet className="w-8 h-8 text-gold" /> Budget Tracker
            </h1>
            <p className="text-white/60 text-sm mt-2">Track and manage expenses for all your trips</p>
          </motion.div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-gold" />
            </div>
          ) : budgets.length === 0 ? (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <Card className="bg-white/95 backdrop-blur-xl border border-white/30 shadow-lg">
                <CardContent className="py-16 text-center">
                  <div className="w-16 h-16 bg-ocean/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Wallet className="w-8 h-8 text-ocean/40" />
                  </div>
                  <h3 className="font-serif text-xl text-ocean font-bold mb-2">No Budget Data Yet</h3>
                  <p className="text-charcoal/50 text-sm mb-6 max-w-md mx-auto">
                    Complete a trip and use the Track Budget feature to start recording your expenses.
                  </p>
                  <Button onClick={() => navigate('/dashboard')}
                    className="bg-gold hover:bg-gold-dark text-ocean font-semibold rounded-lg px-6"
                    data-testid="go-to-trips-budgets">Go to My Trips</Button>
                </CardContent>
              </Card>
            </motion.div>
          ) : (
            <>
              {/* Trips with expenses */}
              {withExpenses.length > 0 && (
                <>
                  <h2 className="text-white/80 text-sm font-semibold uppercase tracking-wider mb-4">Active Budgets ({withExpenses.length})</h2>
                  <motion.div initial="hidden" animate="show" variants={stagger}
                    className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
                    {withExpenses.map(b => (
                      <BudgetCard key={b.itinerary_id} budget={b} onRefresh={fetchBudgets} />
                    ))}
                  </motion.div>
                </>
              )}

              {/* Trips without expenses */}
              {withoutExpenses.length > 0 && (
                <>
                  <h2 className="text-white/50 text-sm font-semibold uppercase tracking-wider mb-4">Start Tracking ({withoutExpenses.length})</h2>
                  <motion.div initial="hidden" animate="show" variants={stagger}
                    className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {withoutExpenses.map(b => (
                      <BudgetCard key={b.itinerary_id} budget={b} onRefresh={fetchBudgets} />
                    ))}
                  </motion.div>
                </>
              )}
            </>
          )}

          <div className="flex justify-center mt-8">
            <button onClick={() => navigate('/dashboard')} className="text-white/70 hover:text-white border border-white/30 hover:bg-white/10 rounded-full px-6 py-2 text-sm font-medium flex items-center gap-2 transition-all" data-testid="back-to-dashboard-budgets-bottom">
              <ArrowLeft className="w-4 h-4" /> Back to Dashboard
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
